import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API_BASE } from '../config';
import { api } from '../api';
import { useTheme } from '../context/ThemeContext';
import { useAlert } from '../hooks/useAlert';
import { useGuestGate } from '../context/GuestGateContext';
import { kycLinkFromError } from '../context/KycContext';
import { SkeletonForm } from '../components/Skeleton';
import { PageHeader } from '../components/Form';
import { AmountInput, EmptyState, MoneySummary, ReviewSheet, SectionHeader } from '../components/PlayerUI';
import Button from '../components/Button';
import Icon from '../components/Icon';

const MIN_AMOUNT = 5;
const MAX_AMOUNT = 1000;

// Solar methods: CashApp-style $5 floor, card $10. Manual methods keep $5.
function minForMethod(method: { name: string; gateway?: string | null } | null): number {
  if (method?.gateway === 'thesolar' && !/cash\s*app/i.test(method.name)) return 10;
  return MIN_AMOUNT;
}

interface PaymentMethod { id: string; name: string; paymentTag: string | null; logoUrl: string | null; gateway?: string | null }
interface PaymentAccount { id: string; label: string; deepLink: string }

export default function WalletTopUp() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { alert } = useAlert();
  const { requireAuth } = useGuestGate();
  const [methods, setMethods] = useState<PaymentMethod[]>([]);
  const [method, setMethod] = useState<PaymentMethod | null>(null);
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    axios.get<PaymentMethod[]>(`${API_BASE}/payment-methods`)
      .then(response => {
        setMethods(response.data);
        setMethod(response.data[0] || null);
      })
      .catch(() => alert(t('wallet.errorTitle'), t('wallet.loadMethodsFailed')))
      .finally(() => setLoading(false));
  }, [alert, t]);

  const numericAmount = Number(amount);
  const minAmount = minForMethod(method);
  const validAmount = Number.isInteger(numericAmount) && numericAmount >= minAmount && numericAmount <= MAX_AMOUNT;

  const openReview = () => {
    if (!method) return alert(t('wallet.missingInfoTitle'), t('wallet.selectMethodMessage'));
    if (!validAmount) return alert(t('wallet.selectAmountTitle'), t('wallet.amountRangeMessage', { min: minAmount }));
    setReviewOpen(true);
  };

  const openProviderCheckout = async () => {
    if (!method || !validAmount) return;
    const checkoutWindow = window.open('', '_blank');
    try {
      const response = await api.post<{ paymentLink: string }>('/orders/topup/thesolar', {
        paymentMethodId: method.id,
        amount: numericAmount,
        target: 'wallet',
      });
      if (!response.data?.paymentLink) throw new Error(t('wallet.orderRecordFailed'));
      if (checkoutWindow) checkoutWindow.location.href = response.data.paymentLink;
      else window.location.href = response.data.paymentLink;
      navigate('/transactions');
    } catch (error: any) {
      const kyc = kycLinkFromError(error);
      if (kyc && checkoutWindow) {
        checkoutWindow.location.href = kyc;
        navigate('/transactions');
        return;
      }
      checkoutWindow?.close();
      alert(t('wallet.orderErrorTitle'), error?.response?.data?.message || error?.message || t('wallet.orderRecordFailed'));
    }
  };

  const recordManualPayment = async () => {
    if (!method || !validAmount) return;
    let account: PaymentAccount;
    try {
      const response = await api.get<PaymentAccount>(`/payment-methods/${method.id}/random-account`);
      account = response.data;
    } catch (error: any) {
      alert(t('wallet.noPaymentAccountTitle'), error?.response?.data?.message || t('wallet.noPaymentAccountMessage'));
      return;
    }

    try { window.open(account.deepLink, '_blank', 'noopener'); } catch { /* Browser may block external applications. */ }
    try {
      await api.post('/orders/topup', {
        paymentMethodId: method.id,
        paymentAccountId: account.id,
        amount: numericAmount,
        target: 'wallet',
        paymentSource: 'method',
      });
      setSuccess(true);
      setAmount('');
    } catch (error: any) {
      alert(t('wallet.orderErrorTitle'), error?.response?.data?.message || t('wallet.orderRecordFailed'));
    }
  };

  const confirm = async () => {
    setReviewOpen(false);
    setSubmitting(true);
    try {
      if (method?.gateway === 'thesolar') await openProviderCheckout();
      else await recordManualPayment();
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <main className="sls-page">
        <PageHeader title={t('wallet.title')} />
        <div className="sls-page-content" style={{ textAlign: 'center', paddingTop: 48 }}>
          <div style={{ width: 64, height: 64, margin: '0 auto 18px', borderRadius: 32, background: colors.accentSoft, display: 'grid', placeItems: 'center' }}>
            <Icon name="check" size={26} color={colors.accent} />
          </div>
          <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700 }}>{t('wallet.redesign.successTitle')}</h1>
          <p style={{ margin: '10px auto 24px', maxWidth: 360, fontSize: 17, lineHeight: 1.5 }}>{t('wallet.redesign.successBody')}</p>
          <Button label={t('wallet.redesign.viewHistory')} onClick={() => navigate('/transactions')} />
          <Button label={t('wallet.redesign.addMore')} variant="secondary" onClick={() => setSuccess(false)} style={{ marginTop: 10 }} />
        </div>
      </main>
    );
  }

  return (
    <main className="sls-page">
      <PageHeader title={t('wallet.title')} />
      {loading ? <div className="sls-page-content"><SkeletonForm fields={3} /></div> : (
        <div className="sls-page-content">
          <p style={{ margin: '0 0 24px', fontSize: 17, lineHeight: 1.5, color: colors.textSub }}>{t('wallet.redesign.subtitle')}</p>

          <SectionHeader title={t('wallet.redesign.amount')} />
          <AmountInput value={amount} onChange={setAmount} suggestions={[20, 50, 100]} />
          {amount && !validAmount ? <p style={{ margin: '8px 0 0', fontSize: 14, color: colors.text }}>{t('wallet.amountRangeMessage', { min: minAmount })}</p> : null}

          <SectionHeader title={t('wallet.redesign.payWith')} />
          {methods.length ? methods.map(paymentMethod => (
            <label key={paymentMethod.id} style={{ minHeight: 64, display: 'flex', alignItems: 'center', gap: 12, borderBottom: `1px solid ${colors.line}`, cursor: 'pointer' }}>
              <input type="radio" name="payment-method" checked={method?.id === paymentMethod.id} onChange={() => setMethod(paymentMethod)} style={{ width: 20, height: 20, accentColor: colors.accent }} />
              {paymentMethod.logoUrl ? <img src={paymentMethod.logoUrl} alt="" style={{ width: 32, height: 32, objectFit: 'contain' }} /> : <span style={{ width: 32, height: 32, borderRadius: 16, background: colors.line, display: 'grid', placeItems: 'center' }}><Icon name="credit-card" size={15} color={colors.text} /></span>}
              <span style={{ fontSize: 17, fontWeight: 600, color: colors.text }}>{paymentMethod.name}</span>
            </label>
          )) : <EmptyState icon="credit-card" title={t('wallet.redesign.noMethods')} body={t('wallet.redesign.noMethodsBody')} helpLink={{ label: t('wallet.redesign.getHelp'), onPress: () => navigate('/faq') }} />}

          <div style={{ marginTop: 28 }}>
            <MoneySummary rows={[
              { label: t('wallet.redesign.youAdd'), value: `$${(validAmount ? numericAmount : 0).toFixed(2)}` },
              { label: t('wallet.redesign.receives'), value: `$${(validAmount ? numericAmount : 0).toFixed(2)}`, emphasis: true },
            ]} />
          </div>
          <Button label={t('wallet.redesign.continuePayment')} disabled={!method || !validAmount} onClick={() => requireAuth(openReview)} style={{ marginTop: 24 }} />
        </div>
      )}

      <ReviewSheet
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        amount={`$${(validAmount ? numericAmount : 0).toFixed(2)}`}
        source={method?.name || ''}
        destination={t('wallet.redesign.destination')}
        timing={t('wallet.redesign.timing')}
        confirmLabel={method?.gateway === 'thesolar' ? t('wallet.redesign.openCheckout') : t('wallet.redesign.openPayment', { method: method?.name })}
        loading={submitting}
        onConfirm={confirm}
      />
    </main>
  );
}
